/**
 * Coverage & gaps: which partners have gone quiet.
 *
 * Buckets come from v_partner_stats.activity, computed off the newest newsletter:
 * active <= 90 days, slowing <= 1 year, dormant beyond that, none = never sent one.
 *
 * The four tiles and the chart are both ways into the filters below them: a tile picks a
 * bucket, a segment of the chart picks a bucket and a country at once. Everything they
 * set is a filter that was already on the panel, so a selection can always be seen and
 * undone there as well.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import { BarsChart } from '../components/charts'
import { CountrySelect, EnumSelect, useHiddenMatches } from '../components/Select'
import {
  Card, Tile, Field, Badge, Empty, QueryState, ActivityBadge, SearchInput, Filters, FilterActions, countFilters,
  useSort, fmtInt, relDays, downloadCsv, CountryLabel, AlumniToggle, AlumniHidden,
} from '../components/ui'
import { DataTable } from '../components/DataTable'
import { useRelabel, useCode } from '../lib/terms'

/**
 * The four activity buckets, in the order they run from writing to silent. One list for
 * the tiles at the top and the bucket filter below them, so the words on a tile are the
 * words in the select and neither can be renamed without the other.
 */
const BUCKETS = [
  { key: 'active', label: 'Active', note: 'wrote within 90 days', tone: 'ok' },
  { key: 'slowing', label: 'Slowing', note: 'within the last year', tone: 'warn' },
  { key: 'dormant', label: 'Dormant', note: 'over a year ago', tone: 'danger' },
  { key: 'none', label: 'Never wrote', note: 'in the registry only' },
]

export default function CoverageReport() {
  const code = useCode()
  const t = useRelabel()
  // A silence only means something for a partner unit still expected to write, so an
  // alumni one has nothing to report here and is left out until the toggle asks.
  const [withAlumni, setWithAlumni] = useState(false)
  const query = useApi(
    ['coverage', withAlumni],
    `stats/coverage${withAlumni ? '?include_alumni=1' : ''}`
  )
  const [activity, setActivity] = useState(null)
  const [countryIds, setCountryIds] = useState([])
  const [q, setQ] = useState('')
  // The ids of units whose real name matches — the names themselves never reach this page.
  const hidden = useHiddenMatches(q)
  const { sort, dir, onSort } = useSort('days_since', 'desc', ['newsletter_count', 'first_newsletter', 'last_newsletter', 'days_since'])

  const clearFilters = () => {
    setActivity(null)
    setCountryIds([])
    setQ('')
    setWithAlumni(false)
  }
  const activeFilters = countFilters({ activity, countryIds, q, withAlumni })

  /* The table's rows: filtered, then sorted, here rather than inside the render below so
     the header's CSV button exports exactly what the table shows, in its order. Sorted in
     the browser — this report already holds every unit — with blanks always last, so
     "never wrote" does not lead a list sorted by its last update. */
  const rows = (query.data?.rows ?? [])
    .filter((r) => {
      if (activity && r.activity !== activity) return false
      if (countryIds.length && !countryIds.includes(r.country_id)) return false
      if (q.trim()) {
        const needle = q.trim().toLowerCase()
        if (
          !String(r.s4ca_code).toLowerCase().includes(needle) &&
          !String(r.display_name ?? '').toLowerCase().includes(needle) &&
          hidden?.has(r.partner_id) !== true
        )
          return false
      }
      return true
    })
    .sort((a, b) => {
      const x = a[sort]
      const y = b[sort]
      if (x == null || x === '') return y == null || y === '' ? 0 : 1
      if (y == null || y === '') return -1
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))
      return dir === 'desc' ? -c : c
    })

  const exportCsv = () =>
    downloadCsv('coverage.csv', rows, [
      { key: 's4ca_code', label: 'S4CA Code' },
      { key: 'display_name', label: 'Name' },
      { key: 'country_name', label: 'Country' },
      { key: 'newsletter_count', label: 'Updates' },
      { key: 'last_newsletter', label: 'Last update' },
      { key: 'days_since', label: 'Days since' },
      { key: 'activity', label: 'Activity' },
    ])

  const columns = [
    {
      key: 's4ca_code',
      label: 'Code',
      sortKey: 's4ca_code',
      render: (r) => (
        <Link className="mono" to={`/partners/${r.partner_id}`}>
          {code(r.s4ca_code)}
        </Link>
      ),
    },
    {
      key: 'display_name',
      label: 'Name',
      sortKey: 'display_name',
      render: (r) => (
        <>
          {code(r.display_name)}
          {r.is_orphan ? <Badge tone="warn" style={{ marginLeft: 4 }}>stub</Badge> : null}
        </>
      ),
    },
    {
      key: 'country_name',
      label: 'Country',
      sortKey: 'country_name',
      render: (r) => <CountryLabel id={r.country_id} name={r.country_name} flag={r.flag_emoji} />,
    },
    {
      key: 'newsletter_count',
      label: 'Updates',
      sortKey: 'newsletter_count',
      numeric: true,
      render: (r) =>
        r.newsletter_count ? (
          <Link to={`/reports/newsletters-by-partner?partner=${r.partner_id}`}>{fmtInt(r.newsletter_count)}</Link>
        ) : (
          <span className="subtle">0</span>
        ),
    },
    { key: 'first_newsletter', label: 'First', sortKey: 'first_newsletter', cellClass: 'small nowrap', render: (r) => r.first_newsletter ?? '—' },
    {
      key: 'last_newsletter',
      label: 'Last',
      sortKey: 'last_newsletter',
      cellClass: 'small nowrap',
      render: (r) =>
        r.last_newsletter ? (
          <>
            {r.last_newsletter}
            <div className="tiny subtle">{relDays(r.last_newsletter)}</div>
          </>
        ) : (
          '—'
        ),
    },
    {
      key: 'days_since',
      label: 'Days silent',
      sortKey: 'days_since',
      numeric: true,
      render: (r) => (r.days_since != null ? fmtInt(r.days_since) : '—'),
    },
    { key: 'activity', label: 'Bucket', sortKey: 'activity', render: (r) => <ActivityBadge activity={r.activity} /> },
  ]

  return (
    <PageHeader
      title="Coverage & Gaps"
      sub={
        <>
          How long since each partner unit last wrote
          <AlumniHidden count={query.data?.alumniHidden} noun="partner units" />
        </>
      }
      actions={
        <button className="btn btn-sm" onClick={exportCsv} disabled={!rows.length}>
          ↓ CSV
        </button>
      }
    >
      <QueryState query={query}>
        {(d) => {
          const buckets = Object.fromEntries(d.buckets.map((b) => [b.activity, b.n]))

          // Dormant partners grouped by country, to show where attention is needed.
          const byCountry = dormantByCountry(d.rows)

          /* A bucket on its own, from a tile: picking the one already applied clears it,
             so the tiles read as the toggles their pressed state makes them look like. */
          const pickBucket = (bucket) => setActivity((cur) => (cur === bucket ? null : bucket))

          /* A segment of the chart is a bucket and a country together, which is the whole
             point of the chart being there — "who is dormant, and where". Both filters are
             set at once, and clicking the same segment again lifts both.

             A country the archive has no row for groups under "Unknown", and there is no
             id to filter on: that segment sets the bucket alone. Rare enough to be worth
             allowing rather than making the bar inert. */
          const pickSegment = (country, bucket) => {
            const row = byCountry.find((c) => c.country === country)
            const on = activity === bucket && countryIds.length === 1 && countryIds[0] === row?.country_id
            setActivity(on ? null : bucket)
            setCountryIds(on || row?.country_id == null ? [] : [row.country_id])
          }

          const bucketLabel = { active: 'active', slowing: 'slowing', dormant: 'dormant', none: 'never wrote' }
          /* What is selected, in the words the tiles and the table use, so the card says
             what was picked rather than leaving the fade to say it. Written for a bucket
             set any of the three ways — a pressed tile always clears on a second click,
             whether the chart, the tile or the select put it there. */
          const picked = activity
            ? `${fmtInt(rows.length)} ${bucketLabel[activity]}` +
              (countryIds.length === 1
                ? ` in ${d.rows.find((r) => r.country_id === countryIds[0])?.country_name ?? 'one country'}`
                : '') +
              ' listed below — click the same tile or segment again to clear'
            : null

          return (
            <div className="col">
              {/* Each tile filters the table to its bucket. They are counts of the whole
                  report either way — the number on a tile is what that bucket holds, not
                  what the filters have left of it, so the four of them go on saying what
                  the archive looks like while one of them is applied. */}
              <div className="grid grid-4">
                {BUCKETS.map((b) => {
                  const n = buckets[b.key] ?? 0
                  return (
                    <Tile
                      key={b.key}
                      label={b.label}
                      value={fmtInt(n)}
                      note={b.note}
                      tone={b.tone}
                      /* An empty bucket is not a filter. Nothing in the archive is
                         active while the newest update is over ninety days old, and a
                         tile that filtered the table to nothing would read as a fault
                         rather than as the zero it is already showing. */
                      onClick={n ? () => pickBucket(b.key) : undefined}
                      active={activity === b.key}
                      title={
                        !n
                          ? `No partner units are ${b.label.toLowerCase()}`
                          : activity === b.key
                            ? `Showing ${b.label.toLowerCase()} partner units — click to show all again`
                            : `Show only the ${b.label.toLowerCase()} partner units`
                      }
                    />
                  )
                })}
              </div>

              <div className="grid grid-2">
                <Card
                  title="Dormant and Silent Partner Units by Country"
                  sub={picked ?? 'Click a segment to list that bucket in that country'}
                >
                  {byCountry.length ? (
                    <BarsChart
                      data={byCountry}
                      x="country"
                      series={[
                        { key: 'dormant', label: 'Dormant' },
                        { key: 'none', label: 'Never wrote' },
                      ]}
                      horizontal
                      stacked
                      height={300}
                      onPick={pickSegment}
                      /* Only lit while the selection is one this chart can show: a bucket
                         with no country is a row of the table, not a segment of a bar. */
                      activeX={
                        countryIds.length === 1
                          ? byCountry.find((c) => c.country_id === countryIds[0])?.country
                          : null
                      }
                      activeKey={activity}
                    />
                  ) : (
                    <Empty title="Nothing dormant" />
                  )}
                </Card>
                <Card title="Longest Silences" sub="Partner units that have written at least once">
                  <div className="table-wrap flush">
                    <table className="data compact">
                      <thead>
                        <tr>
                          <th>{t('Partner Unit')}</th>
                          <th>Last update</th>
                          <th className="right">Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.rows
                          .filter((r) => r.last_newsletter)
                          .slice(0, 14)
                          .map((r) => (
                            <tr key={r.partner_id}>
                              <td>
                                <Link className="mono" to={`/partners/${r.partner_id}`}>
                                  {code(r.s4ca_code)}
                                </Link>{' '}
                                <span className="small">{code(r.display_name)}</span>
                              </td>
                              <td className="small nowrap">{r.last_newsletter}</td>
                              <td className="num">{fmtInt(r.days_since)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>

              {/* Above the panel, as on every list. */}
              <SearchInput
                id="cov-q"
                value={q}
                onChange={setQ}
                placeholder="Code or name…"
                aria-label="Search partner units"
              />
              <Filters id="coverage" active={activeFilters} onClear={clearFilters}>
                <Field label="Activity bucket" htmlFor="cov-act">
                  <EnumSelect
                    inputId="cov-act"
                    value={activity}
                    onChange={setActivity}
                    options={BUCKETS.map((b) => ({ value: b.key, label: b.label }))}
                  />
                </Field>
                <Field label="Country" htmlFor="cov-country">
                  <CountrySelect inputId="cov-country" multi value={countryIds} onChange={setCountryIds} />
                </Field>
                <AlumniToggle
                  checked={withAlumni}
                  onChange={setWithAlumni}
                  where="silences"
                />
                <FilterActions
                  active={activeFilters}
                  onClear={clearFilters}
                  note={t(`${fmtInt(rows.length)} of ${fmtInt(d.rows.length)} partner units`)}
                />
              </Filters>

              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(r) => r.partner_id}
                sort={sort}
                dir={dir}
                onSort={onSort}
                empty={<Empty title="No partner units match these filters" />}
              />
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}

/**
 * Roll dormant / never-wrote counts up by country for the stacked bar chart.
 * Plain function, not a hook — it runs inside QueryState's render callback, where
 * a hook would register against the wrong component and change count between the
 * loading and loaded renders.
 *
 * `country_id` rides along beside the name the chart draws: a click on a segment has
 * only the name to go on, and the filter it sets takes an id. A row with no country
 * keeps a null id — that segment can pick its bucket but not a country.
 */
function dormantByCountry(rows) {
  const m = new Map()
  for (const r of rows) {
    if (r.activity !== 'dormant' && r.activity !== 'none') continue
    const key = r.country_name ?? 'Unknown'
    if (!m.has(key)) m.set(key, { country: key, country_id: r.country_id ?? null, dormant: 0, none: 0 })
    m.get(key)[r.activity] += 1
  }
  return [...m.values()].sort((a, b) => b.dormant + b.none - (a.dormant + a.none)).slice(0, 12)
}
